// Shared Google Ads click-id (gclid) handling. The marketing site captures the
// click id from the landing URL and persists it (once marketing consent is
// given) on the registrable parent domain; the app reads it at onboarding to
// attribute the sign-up conversion, then clears it.
//
// The click id arrives in a custom `sc_gclid` query param rather than the
// standard `gclid`: Safari's ITP strips the known `gclid` parameter from URLs,
// but leaves an unrecognised custom name intact.

import Cookies from 'js-cookie';
import { CONSENT_EVENT, readStoredConsent } from './consentCookie';

const GCLID_QUERY_PARAM = 'sc_gclid';
const GCLID_COOKIE = '__sc_gclid';
// Google Ads offline conversions can be uploaded up to 90 days after the click.
const MAX_AGE_DAYS = 90;
// Registrable domain shared by the marketing site and the app so the cookie set
// by one is readable by the other's subdomain.
const COOKIE_DOMAIN = '.starkcrawl.com';

// Host-only cookie in local dev (localhost is shared across ports anyway).
const cookieDomain = (): string | undefined => {
  const { hostname } = window.location;

  if (hostname === 'localhost' || /^\d+(\.\d+){3}$/.test(hostname)) {
    return undefined;
  }

  return COOKIE_DOMAIN;
};

const setCookie = (gclid: string): void => {
  Cookies.set(GCLID_COOKIE, gclid, {
    path: '/',
    expires: MAX_AGE_DAYS,
    domain: cookieDomain(),
    sameSite: 'lax',
    secure: window.location.protocol === 'https:',
  });
};

const hasMarketingConsent = (): boolean =>
  readStoredConsent()?.marketing === true;

export const getGclid = (): string | undefined => {
  if (typeof document === 'undefined') return undefined;

  const value = Cookies.get(GCLID_COOKIE);

  if (!value) return undefined;

  return value;
};

// Best-effort clear once the conversion has been recorded, so a later signup on
// the same browser isn't re-attributed to a stale click.
export const clearGclid = (): void => {
  if (typeof document === 'undefined') return;

  Cookies.remove(GCLID_COOKIE, { path: '/', domain: cookieDomain() });

  // Also clear any host-only copy (e.g. one set without a domain in dev).
  Cookies.remove(GCLID_COOKIE, { path: '/' });
};

// Held in memory (not persisted) until marketing consent is granted, so we never
// store the click id ahead of consent. Survives client-side route changes; only
// a full reload before consenting loses it.
let pendingGclid: string | null = null;

// Captures the click id from the landing URL and persists it once the visitor
// grants marketing consent, keeping it consistent with the Consent Mode setup.
// Call once on the marketing site's client entry.
export const initGclidCapture = (): void => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);

  const gclid = params.get('gclid') ?? params.get(GCLID_QUERY_PARAM);

  if (gclid) pendingGclid = gclid;

  const flush = (): void => {
    if (pendingGclid && hasMarketingConsent()) {
      setCookie(pendingGclid);

      pendingGclid = null;
    }
  };

  // Returning visitor who already consented — persist immediately.
  flush();

  // Otherwise wait for the cookie banner to grant marketing consent.
  window.addEventListener(CONSENT_EVENT, flush);
};
